import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import './SettingsPage.css';

interface SettingsPageProps {
  userData: any;
  onUserDataUpdate?: (data: any) => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ userData, onUserDataUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  // Username change
  const [username, setUsername] = useState(userData?.username || '');
  
  // 2FA state
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaQrUrl, setMfaQrUrl] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaUnenrollCode, setMfaUnenrollCode] = useState('');
  const [mfaUnenrollId, setMfaUnenrollId] = useState<string | null>(null);
  const [showUnenroll, setShowUnenroll] = useState(false);

  useEffect(() => {
    checkMfaStatus();
  }, []);

  async function checkMfaStatus() {
    if (!supabase) return;
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = factors?.all?.find((f: any) => f.status === 'verified' && f.type === 'totp');
      setMfaEnabled(!!totp);
      if (totp) setMfaUnenrollId(totp.id);
    } catch {}
  }

  async function handleChangePassword() {
    if (newPassword !== confirmNewPassword) { setMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    if (newPassword.length < 8) { setMsg({ type: 'error', text: 'Password must be at least 8 characters' }); return; }
    if (!supabase) return;
    setLoading(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setMsg({ type: 'success', text: 'Password updated successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update password' });
    }
    setLoading(false);
  }

  async function handleUpdateUsername() {
    if (!supabase || !username.trim()) return;
    setLoading(true);
    setMsg(null);
    try {
      const { error } = await supabase.from('users').update({ username: username.trim() }).eq('id', userData.id);
      if (error) throw error;
      setMsg({ type: 'success', text: 'Username updated!' });
      if (onUserDataUpdate) onUserDataUpdate({ ...userData, username: username.trim() });
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update username' });
    }
    setLoading(false);
  }

  // ===== 2FA ENROLLMENT =====
  async function handleEnrollMfa() {
    if (!supabase) return;
    setMfaEnrolling(true);
    setMsg(null);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'SimForge Authenticator' });
      if (error) throw error;
      setMfaFactorId(data.id);
      setMfaQrUrl(data.totp.qr_code);
      setMfaSecret(data.totp.secret);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to enroll 2FA' });
      setMfaEnrolling(false);
    }
  }

  async function handleVerifyMfa() {
    if (!supabase || !mfaFactorId || mfaVerifyCode.length !== 6) return;
    setLoading(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: mfaFactorId, code: mfaVerifyCode });
      if (error) throw error;
      setMfaEnabled(true);
      setMfaUnenrollId(mfaFactorId);
      setMfaEnrolling(false);
      setMfaQrUrl(null);
      setMfaSecret(null);
      setMfaVerifyCode('');
      setMsg({ type: 'success', text: '2FA enabled! Your account is now more secure.' });
    } catch (err: any) {
      setMsg({ type: 'error', text: 'Invalid code. Try again.' });
    }
    setLoading(false);
  }

  async function handleUnenrollMfa() {
    if (!supabase || !mfaUnenrollId) return;
    setLoading(true);
    setMsg(null);
    try {
      // Verify code first to get adequate auth level
      const { error: challengeError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaUnenrollId,
        code: mfaUnenrollCode,
      });
      if (challengeError) throw challengeError;

      const { error } = await supabase.auth.mfa.unenroll({ factorId: mfaUnenrollId });
      if (error) throw error;
      setMfaEnabled(false);
      setMfaUnenrollId(null);
      setShowUnenroll(false);
      setMfaUnenrollCode('');
      setMsg({ type: 'success', text: '2FA has been disabled.' });
    } catch (err: any) {
      setMsg({ type: 'error', text: 'Invalid code. Cannot disable 2FA without verification.' });
    }
    setLoading(false);
  }

  function cancelMfaEnroll() {
    setMfaEnrolling(false);
    setMfaQrUrl(null);
    setMfaSecret(null);
    setMfaVerifyCode('');
    setMfaFactorId(null);
  }

  return (
    <div className="settings-page">
      <h1>⚙️ Account Settings</h1>

      {msg && (
        <div className={`settings-msg ${msg.type}`}>
          {msg.text}
        </div>
      )}

      {/* ACCOUNT INFO */}
      <div className="settings-card">
        <h2>👤 Account Info</h2>
        <div className="settings-row">
          <label>Email</label>
          <span className="settings-value">{userData?.email || '—'}</span>
        </div>
        <div className="settings-row">
          <label>User ID</label>
          <span className="settings-value settings-muted">{userData?.id?.substring(0, 12)}...</span>
        </div>
        <div className="settings-row">
          <label>Username</label>
          <div className="settings-edit-row">
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="settings-input" />
            <button className="settings-btn" onClick={handleUpdateUsername} disabled={loading || !username.trim()}>Save</button>
          </div>
        </div>
        <div className="settings-row">
          <label>$BALLS Balance</label>
          <span className="settings-value settings-balls">{(userData?.balls_balance || 0).toLocaleString()}</span>
        </div>
      </div>

      {/* CHANGE PASSWORD */}
      <div className="settings-card">
        <h2>🔑 Change Password</h2>
        <div className="settings-field">
          <label>New Password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 8 characters" className="settings-input" />
        </div>
        <div className="settings-field">
          <label>Confirm New Password</label>
          <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="Re-enter password" className="settings-input" />
        </div>
        <button className="settings-btn primary" onClick={handleChangePassword} disabled={loading || !newPassword || !confirmNewPassword}>
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </div>

      {/* 2FA / MFA */}
      <div className="settings-card">
        <h2>🔐 Two-Factor Authentication</h2>
        
        {mfaEnabled ? (
          <div className="mfa-status enabled">
            <span className="mfa-badge">✅ 2FA Enabled</span>
            <p className="mfa-desc">Your account requires an authenticator code when signing in.</p>
            {!showUnenroll ? (
              <button className="settings-btn danger" onClick={() => setShowUnenroll(true)}>Disable 2FA</button>
            ) : (
              <div className="mfa-unenroll">
                <p>Enter your authenticator code to disable 2FA:</p>
                <div className="settings-edit-row">
                  <input type="text" value={mfaUnenrollCode} onChange={(e) => setMfaUnenrollCode(e.target.value.replace(/\D/g, '').slice(6))} placeholder="000000" maxLength={6} className="settings-input mfa-code-input" />
                  <button className="settings-btn danger" onClick={handleUnenrollMfa} disabled={loading || mfaUnenrollCode.length !== 6}>Disable</button>
                  <button className="settings-btn" onClick={() => { setShowUnenroll(false); setMfaUnenrollCode(''); }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ) : mfaEnrolling ? (
          <div className="mfa-enroll">
            <p className="mfa-desc">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
            
            {mfaQrUrl && (
              <div className="mfa-qr">
                <img src={mfaQrUrl} alt="2FA QR Code" className="qr-image" />
              </div>
            )}
            
            {mfaSecret && (
              <div className="mfa-secret">
                <label>Manual entry key:</label>
                <code>{mfaSecret}</code>
              </div>
            )}

            <p className="mfa-desc">Enter the 6-digit code from your authenticator:</p>
            <div className="settings-edit-row">
              <input type="text" value={mfaVerifyCode} onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(6))} placeholder="000000" maxLength={6} className="settings-input mfa-code-input" />
              <button className="settings-btn primary" onClick={handleVerifyMfa} disabled={loading || mfaVerifyCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button className="settings-btn" onClick={cancelMfaEnroll}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mfa-status disabled">
            <span className="mfa-badge off">⚠️ 2FA Not Enabled</span>
            <p className="mfa-desc">Add an extra layer of security. You'll need an authenticator code to sign in.</p>
            <button className="settings-btn primary" onClick={handleEnrollMfa}>Enable 2FA</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
